/**
 * Funnel analytics (WOW · measure). Pure TypeScript shared by the client (typed events, legacy
 * event mapping, offline buffer rules, D1/D7 markers) and the server (funnel + retention maths).
 *
 * Privacy: events carry no names, emails or free text; props are short primitives only
 * (`sanitizeProps`). The client only uploads after a grown-up allowed analytics in onboarding.
 */
import { dayKey } from './progress';

/** Every event the funnel understands. Add new names at the end (the server stores the string). */
export const FUNNEL_EVENT_NAMES = [
  'app_open',
  'onboarding_step',
  'onboarding_complete',
  'profile_created',
  'lesson_start',
  'lesson_step_done',
  'capture',
  'feedback_shown',
  'lesson_complete',
  'first_drawing_done',
  'creature_alive',
  'return_d1',
  'return_d7',
  'paywall_view',
  'paywall_convert',
  'parent_area_open',
  'parent_report_share',
  'timelapse_export',
  'family_share',
  /* ---- Monetization (MONETIZATION-PLAN §11.3). Client: paywall / purchase / gate / wish. ---- */
  'paywall_close',
  'paywall_plan_select',
  'purchase_start',
  'purchase_fail',
  'purchase_pending',
  'restore',
  'parental_gate_shown',
  'parental_gate_pass',
  'parental_gate_fail',
  'locked_tap',
  'wish_add',
  'wish_view_parent',
  'paywall_exit_survey',
  'manage_subscription_open',
  'cancel_flow_step',
  /* ---- Lifecycle (server, from webhooks / mock store: the source of truth). ---- */
  'trial_start',
  'trial_convert',
  'trial_cancel',
  'purchase_complete',
  'renewal',
  'product_change',
  'billing_issue',
  'grace_recovered',
  'expiration',
  'refund',
  'subscription_paused',
  'winback_offer_shown',
  'winback_offer_redeemed',
  'family_shared_grant',
  'iap_purchase',
  /* ---- Viral loops ---- */
  'invite_create',
  'invite_open',
  'invite_install',
  'invite_accept',
  'invite_paid',
  'invite_reward_granted',
  'invite_void',
  'gift_checkout',
  'gift_purchase',
  'gift_redeem',
  'gift_refund',
  'share_link_view',
  /* ---- Parent value ---- */
  'report_email_sent',
  'report_email_open',
  'report_email_click',
  'return_d30',
  'skin_apply',
  'print_order',
  'trial_reminder_sent',
  /* ---- Story layer (IDEAS-BACKLOG §E) ---- */
  'story_meet',
  'story_card',
  'story_narration',
  'story_reaction',
  'story_comic',
  'storybook_open',
  'storybook_share',
  'storybook_export',
  'bedtime_start',
  'bedtime_end',
  /* ---- Playground discovery ("All") ---- */
  'playground_surprise',
  'playground_see_more',
  /* ---- Performance telemetry (docs/PERF.md) ---- */
  'perf_startup',
  'perf_lesson_open',
  'perf_sample',
] as const;
export type FunnelEventName = (typeof FUNNEL_EVENT_NAMES)[number];
export const isFunnelEventName = (s: string): s is FunnelEventName => (FUNNEL_EVENT_NAMES as readonly string[]).includes(s);

/** Ordered core funnel shown on the dashboard (each step counts installs that also reached every earlier step). */
export const FUNNEL_STEPS: FunnelEventName[] = [
  'app_open',
  'onboarding_complete',
  'lesson_start',
  'capture',
  'first_drawing_done',
  'creature_alive',
  'return_d1',
  'return_d7',
  'paywall_view',
  'paywall_convert',
];

export type EventProps = Record<string, string | number | boolean | null>;

export interface TrackedEvent {
  /** Client-generated id (idempotent upload: the server ignores a repeated id for the same install). */
  id: string;
  name: FunnelEventName;
  /** ISO time on the device. */
  at: string;
  props?: EventProps;
}

export interface EventBatch {
  installId: string;
  appVersion?: string;
  platform?: string;
  lang?: string;
  events: TrackedEvent[];
}

/** Events written by the server (webhooks, mock store, jobs); the client never sends these. */
export const SERVER_EVENT_NAMES: readonly FunnelEventName[] = [
  'trial_start',
  'trial_convert',
  'trial_cancel',
  'purchase_complete',
  'renewal',
  'product_change',
  'billing_issue',
  'grace_recovered',
  'expiration',
  'refund',
  'subscription_paused',
  'family_shared_grant',
  'iap_purchase',
  'invite_paid',
  'invite_reward_granted',
  'invite_void',
  'gift_purchase',
  'gift_refund',
  'trial_reminder_sent',
];

/** Revenue funnel for the dashboard (install → aha → paywall → trial → paid → renewal). */
export const REVENUE_FUNNEL_STEPS: FunnelEventName[] = ['app_open', 'creature_alive', 'paywall_view', 'trial_start', 'trial_convert', 'renewal'];
/** Viral funnel for the dashboard. */
export const VIRAL_FUNNEL_STEPS: FunnelEventName[] = ['invite_create', 'invite_open', 'invite_accept', 'invite_paid', 'invite_reward_granted'];

/**
 * Super-properties attached to every event (first-party, like membership props in store apps) so
 * funnels can be cut by plan / trial / experiment. Ids / enums / numbers only.
 */
export interface SuperProps {
  tier?: 'free' | 'individual' | 'family';
  isTrial?: boolean;
  period?: 'monthly' | 'yearly' | null;
  freeBoundary?: string;
  daysSinceInstall?: number;
  /** Experiment buckets as "exp:bucket,exp2:bucket" (short). */
  buckets?: string;
}

/** Event props + super-props (event props win on a clash; super-props take at most 6 of the 12 slots). */
export function withSuperProps(props: Record<string, unknown> | undefined, sp: SuperProps | null | undefined): EventProps | undefined {
  const supers: Record<string, unknown> = {};
  if (sp) {
    for (const [k, v] of Object.entries(sp)) if (v !== undefined) supers[k] = v;
  }
  const own = sanitizeProps(props) ?? {};
  const room = Math.max(0, 12 - Object.keys(own).length);
  const extra = Object.fromEntries(Object.entries(sanitizeProps(supers) ?? {}).filter(([k]) => !(k in own)).slice(0, Math.min(6, room)));
  const out = { ...extra, ...own };
  return Object.keys(out).length ? out : undefined;
}

/** Upload limits (client batches, server validation). */
export const EVENTS_MAX_BATCH = 100;
/** Offline buffer cap: oldest events are dropped first. */
export const EVENTS_MAX_BUFFER = 500;

const BLOCKED_KEYS = /name|email|text|title|comment|token|password|phone|address/i;

/** Keep ≤ 12 primitive props, short strings, and never anything that looks personal. */
export function sanitizeProps(props: Record<string, unknown> | undefined): EventProps | undefined {
  if (!props) return undefined;
  const out: EventProps = {};
  let n = 0;
  for (const [k, v] of Object.entries(props)) {
    if (n >= 12) break;
    if (BLOCKED_KEYS.test(k) || k.length > 32) continue;
    if (typeof v === 'string') out[k] = v.slice(0, 64);
    else if (typeof v === 'number') {
      if (!Number.isFinite(v)) continue;
      out[k] = Math.round(v * 1000) / 1000;
    } else if (typeof v === 'boolean' || v === null) out[k] = v;
    else continue;
    n++;
  }
  return n ? out : undefined;
}

/** Append to the offline buffer, dropping the oldest beyond `max`. */
export function enqueueEvent(buffer: TrackedEvent[], ev: TrackedEvent, max = EVENTS_MAX_BUFFER): TrackedEvent[] {
  const next = [...buffer, ev];
  return next.length > max ? next.slice(next.length - max) : next;
}

/** Remove the events the server acknowledged (by id). */
export function ackEvents(buffer: TrackedEvent[], sentIds: readonly string[]): TrackedEvent[] {
  if (!sentIds.length) return buffer;
  const s = new Set(sentIds);
  return buffer.filter((e) => !s.has(e.id));
}

/**
 * The app's existing descriptive events (store.logEvent) → typed funnel events. Keeps every screen
 * untouched: the client taps the log stream and maps what it recognises.
 */
export function mapLegacyEvent(name: string, props: Record<string, unknown> = {}): { name: FunnelEventName; props?: EventProps }[] {
  const p = (o: Record<string, unknown>) => sanitizeProps(o);
  switch (name) {
    case 'Onboarding Step Shown':
      return [{ name: 'onboarding_step', props: p({ step: props.step }) }];
    case 'Onboarding Completed':
      return [{ name: 'onboarding_complete', props: p({ calibration: props.calibration }) }];
    case 'Profile Created':
      return [{ name: 'profile_created', props: p({ age: props.age, source: props.source }) }];
    case 'Unit Item Start':
      return props.index === 0 ? [{ name: 'lesson_start', props: p({ miniUnitId: props.miniUnitId, together: props.together }) }] : [];
    case 'Video Step Ready':
      return [{ name: 'lesson_step_done', props: p({ miniUnitId: props.miniUnitId, chapter: props.chapter }) }];
    case 'Drawing Photo Uploaded':
      return [{ name: 'capture', props: p({ miniUnitId: props.miniUnitId }) }];
    case 'Drawing Feedback Received':
      return [{ name: 'feedback_shown', props: p({ miniUnitId: props.miniUnitId, type: props.type, stars: props.stars }) }];
    case 'Unit Completed':
      return [{ name: 'lesson_complete', props: p({ miniUnitId: props.miniUnitId, sparks: props.sparks }) }];
    case 'Creature Alive':
    case 'Creature Came Alive':
      return [{ name: 'creature_alive', props: p({ creature: props.creature ?? props.id }) }];
    case 'View Paywall':
      return [{ name: 'paywall_view', props: p({ from: props.from, variant: props.variant }) }];
    case 'Purchase Completed':
      return [{ name: 'paywall_convert', props: p({ plan: props.plan, period: props.period, store: props.store, trial: props.trial }) }];
    default:
      return [];
  }
}

/* --------------------------------- retention --------------------------------- */

/** Whole local calendar days between two instants (0 = same day). */
export function daysBetween(fromIso: string | Date, to: string | Date): number {
  const a = new Date(fromIso);
  const b = new Date(to);
  const da = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const db = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((db - da) / 86400_000);
}

/** Retention markers due at this app open (each sent once per install). */
export function retentionMarkers(firstLaunch: string, now: Date, sent: readonly string[]): ('return_d1' | 'return_d7')[] {
  const d = daysBetween(firstLaunch, now);
  const out: ('return_d1' | 'return_d7')[] = [];
  if (d === 1 && !sent.includes('return_d1')) out.push('return_d1');
  if (d === 7 && !sent.includes('return_d7')) out.push('return_d7');
  return out;
}

/** Days since the profile was last active (progress.activeDays, local day keys), or null when never. */
export function daysAway(activeDays: readonly string[], now: Date): number | null {
  const today = dayKey(now);
  const past = activeDays.filter((d) => d < today).sort();
  const last = past[past.length - 1];
  if (!last) return null;
  const [y, m, dd] = last.split('-').map(Number);
  return daysBetween(new Date(y!, m! - 1, dd!), now);
}

/* ------------------------------ server-side maths ------------------------------ */

export interface EventRow {
  installId: string;
  name: string;
  at: Date | string;
}

export interface FunnelRow {
  step: FunnelEventName;
  installs: number;
  /** Share of the first step (0..1). */
  ofStart: number;
  /** Share of the previous step (0..1). */
  ofPrev: number;
}

/** Ordered funnel: an install counts at step i when it has steps 0..i (in any order). */
export function computeFunnel(rows: readonly EventRow[], steps: readonly FunnelEventName[] = FUNNEL_STEPS): FunnelRow[] {
  const by = new Map<string, Set<string>>();
  for (const r of rows) {
    let s = by.get(r.installId);
    if (!s) by.set(r.installId, (s = new Set()));
    s.add(r.name);
  }
  const counts = steps.map((_, i) => {
    let n = 0;
    for (const names of by.values()) if (steps.slice(0, i + 1).every((st) => names.has(st))) n++;
    return n;
  });
  return steps.map((step, i) => ({
    step,
    installs: counts[i]!,
    ofStart: counts[0] ? counts[i]! / counts[0] : 0,
    ofPrev: i === 0 ? 1 : counts[i - 1] ? counts[i]! / counts[i - 1]! : 0,
  }));
}

export interface RetentionCohort {
  /** Cohort day (UTC day key of the install's first event). */
  day: string;
  installs: number;
  d1: number;
  d7: number;
}

export interface RetentionSummary {
  cohorts: RetentionCohort[];
  /** Rates over cohorts old enough to be measured (null when none). */
  d1Rate: number | null;
  d7Rate: number | null;
  d1Eligible: number;
  d7Eligible: number;
}

const utcDay = (d: Date) => d.toISOString().slice(0, 10);
const utcDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400_000);

/** Classic day-N retention from activity: an install is retained on day N when it has any event on its install day + N (UTC). */
export function computeRetention(rows: readonly EventRow[], now: Date): RetentionSummary {
  const days = new Map<string, Set<string>>();
  for (const r of rows) {
    const k = utcDay(new Date(r.at));
    let s = days.get(r.installId);
    if (!s) days.set(r.installId, (s = new Set()));
    s.add(k);
  }
  const today = utcDay(now);
  const cohorts = new Map<string, RetentionCohort>();
  let d1E = 0;
  let d1N = 0;
  let d7E = 0;
  let d7N = 0;
  for (const set of days.values()) {
    const sorted = [...set].sort();
    const first = sorted[0]!;
    const c = cohorts.get(first) ?? { day: first, installs: 0, d1: 0, d7: 0 };
    c.installs++;
    const has = (n: number) => sorted.some((d) => utcDiff(first, d) === n);
    const age = utcDiff(first, today);
    if (age >= 1) {
      d1E++;
      if (has(1)) (c.d1++, d1N++);
    }
    if (age >= 7) {
      d7E++;
      if (has(7)) (c.d7++, d7N++);
    }
    cohorts.set(first, c);
  }
  return {
    cohorts: [...cohorts.values()].sort((a, b) => (a.day < b.day ? -1 : 1)),
    d1Rate: d1E ? d1N / d1E : null,
    d7Rate: d7E ? d7N / d7E : null,
    d1Eligible: d1E,
    d7Eligible: d7E,
  };
}

/** Distinct active installs per UTC day for the last `days` days (oldest first). */
export function dailyActive(rows: readonly EventRow[], now: Date, days = 14): { day: string; installs: number }[] {
  const map = new Map<string, Set<string>>();
  for (const r of rows) {
    const k = utcDay(new Date(r.at));
    let s = map.get(k);
    if (!s) map.set(k, (s = new Set()));
    s.add(r.installId);
  }
  const out: { day: string; installs: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const k = utcDay(new Date(now.getTime() - i * 86400_000));
    out.push({ day: k, installs: map.get(k)?.size ?? 0 });
  }
  return out;
}

/* ------------------------------------------------ performance telemetry ------------------------------------------------ */

export interface PerfRow {
  name: string;
  props?: string | Record<string, unknown> | null;
}
export interface PerfStat {
  n: number;
  p50: number | null;
  p75: number | null;
  p95: number | null;
}
export interface PerfSummary {
  /** App start → first screen interactive (ms). */
  startup: PerfStat;
  /** Tap → lesson first item painted (ms). */
  lessonOpen: PerfStat;
  /** Long tasks per minute while visible. */
  longTasksPerMin: PerfStat;
  /** Sampled frames per second (2 s rAF samples). */
  fps: PerfStat;
  /** JS heap MB where the platform reports it (Chrome / Android WebView). */
  heapMB: PerfStat;
  /** DOM nodes on screen at sample time. */
  domNodes: PerfStat;
  /** Share of samples taken in battery saver. */
  lowPowerShare: number | null;
  byPlatform: { platform: string; startupP50: number | null; lessonOpenP50: number | null; n: number }[];
}

const quant = (xs: number[], q: number): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return Math.round(s[Math.min(s.length - 1, Math.floor(q * (s.length - 1) + 0.5))]! * 10) / 10;
};
const stat = (xs: number[]): PerfStat => ({ n: xs.length, p50: quant(xs, 0.5), p75: quant(xs, 0.75), p95: quant(xs, 0.95) });
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Percentiles of the perf_* events (dashboard). */
export function computePerf(rows: readonly PerfRow[]): PerfSummary {
  const get = (r: PerfRow): Record<string, unknown> => {
    if (!r.props) return {};
    if (typeof r.props === 'string') {
      try {
        return JSON.parse(r.props) as Record<string, unknown>;
      } catch {
        return {};
      }
    }
    return r.props;
  };
  const startup: number[] = [];
  const lesson: number[] = [];
  const lt: number[] = [];
  const fps: number[] = [];
  const heap: number[] = [];
  const dom: number[] = [];
  let low = 0;
  let samples = 0;
  const plat = new Map<string, { s: number[]; l: number[] }>();
  const P = (p: unknown) => {
    const k = typeof p === 'string' && p ? p : 'web';
    let v = plat.get(k);
    if (!v) plat.set(k, (v = { s: [], l: [] }));
    return v;
  };
  for (const r of rows) {
    if (!r.name.startsWith('perf_')) continue;
    const p = get(r);
    const ms = num(p.ms);
    if (r.name === 'perf_startup' && ms !== null) (startup.push(ms), P(p.platform).s.push(ms));
    else if (r.name === 'perf_lesson_open' && ms !== null) (lesson.push(ms), P(p.platform).l.push(ms));
    else if (r.name === 'perf_sample') {
      samples++;
      if (p.low === true) low++;
      const a = num(p.longTasksPerMin);
      const f = num(p.fps);
      const h = num(p.heapMB);
      const d = num(p.domNodes);
      if (a !== null) lt.push(a);
      if (f !== null) fps.push(f);
      if (h !== null) heap.push(h);
      if (d !== null) dom.push(d);
    }
  }
  return {
    startup: stat(startup),
    lessonOpen: stat(lesson),
    longTasksPerMin: stat(lt),
    fps: stat(fps),
    heapMB: stat(heap),
    domNodes: stat(dom),
    lowPowerShare: samples ? Math.round((low / samples) * 100) / 100 : null,
    byPlatform: [...plat.entries()].map(([platform, v]) => ({ platform, startupP50: quant(v.s, 0.5), lessonOpenP50: quant(v.l, 0.5), n: v.s.length + v.l.length })),
  };
}
